import ApplicationController from "@mixitone/mvc";
import { isNil, keyBy } from "@mixitone/util";
import type { Column } from "./Column";
import { List } from "@mixitone/oom";

export type TableRecord = { id: string | undefined };

export interface TableState<T extends TableRecord, Filters extends Record<string, any> = object> {
  items: List<T>;
  sortField: string;
  sortDirection: "asc" | "desc";
  selectedItems: Set<string>;
  filters: Partial<Filters>;
  filteredItems: T[];
  /** When set, table shows only this many rows per page. Omit for no pagination. */
  pageSize?: number;
  /** 1-based current page. Only used when pageSize is set. */
  currentPage?: number;
}

const STORAGE_KEY_PREFIX = "mixitone_table_filter_";

/**
 * Base controller for table components that provides common functionality for:
 * - Item filtering (search and custom filters)
 * - Sorting
 * - Selection
 */
export class TableController<
  T extends TableRecord,
  Filters extends Record<string, any> = object,
  CustomState = object,
  Props = object,
> extends ApplicationController<TableState<T, Filters> & CustomState, Props> {
  private columns: Record<string, Column<T>> = {};

  updateColumns(columns: Column<T>[]) {
    this.columns = keyBy(columns, "id");
  }

  // @ts-ignore
  public override get initialState(): TableState<T> {
    // Try to load saved filter state from session storage
    const savedState = this.loadFilterState();

    return {
      items: new List<T>(),
      sortField: savedState?.sortField || "name",
      sortDirection: savedState?.sortDirection || "asc",
      selectedItems: new Set<string>(),
      filters: savedState?.filters || {},
      filteredItems: [],
    };
  }

  /**
   * Override initialize to set up state observation for filter changes
   */
  override async initialize(props: Props) {
    await super.initialize(props);

    // Observe filter-related state changes to save to session storage
    this.observe(() => {
      const { sortField, sortDirection, filters, pageSize, currentPage } = this.state;
      const payload: Parameters<typeof this.saveFilterState>[0] = {
        sortField,
        sortDirection,
        filters,
      };
      if (pageSize != null) payload.pageSize = pageSize;
      if (currentPage != null) payload.currentPage = currentPage;
      this.saveFilterState(payload);
    });

    this.observe(() => {
      this.updateFilteredItems();
    });
  }

  // Selection helpers
  get anySelected(): boolean {
    return this.state.selectedItems.size > 0;
  }

  get allSelected(): boolean {
    return (
      this.filteredItems.length > 0 &&
      this.filteredItems.every((item) => this.state.selectedItems.has(this.getItemId(item)))
    );
  }

  get empty(): boolean {
    return this.state.items.length === 0;
  }

  isSelected(item: T): boolean {
    return this.state.selectedItems.has(this.getItemId(item));
  }

  // Default implementation using SortableRecord's id
  getItemId(item: T): string {
    return item.id || "";
  }

  // Abstract methods that must be implemented by derived controllers
  compareItems(a: T, b: T, field: string): number {
    const column = this.columns[field];
    if (column && typeof column.sortable === "function") {
      return column.sortable(a, b);
    }

    if (field in a && field in b) {
      const aValue = a[field as keyof T];
      const bValue = b[field as keyof T];
      if (isNil(aValue)) return isNil(bValue) ? 0 : -1;
      if (isNil(bValue)) return 1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }

    return 0;
  }

  updateFilteredItems(): void {
    // Don't update if items are empty - wait for data to load
    // This prevents filters from being applied before dependent data is ready
    if (this.state.items.length === 0) {
      this.state.filteredItems = [];
      return;
    }

    let filtered = [...this.state.items];

    // Apply custom filters
    Object.entries(this.state.filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        filtered = this.applyFilter(filtered, key, value);
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      const direction = this.state.sortDirection === "asc" ? 1 : -1;
      return direction * this.compareItems(a, b, this.state.sortField);
    });

    this.state.filteredItems = filtered;
    if (this.state.pageSize != null && this.state.pageSize > 0) {
      const maxPage = Math.ceil(filtered.length / this.state.pageSize) || 1;
      const current = this.state.currentPage ?? 1;
      this.state.currentPage = Math.max(1, Math.min(current, maxPage));
    }
  }
  get filteredItems(): T[] {
    return this.state.filteredItems;
  }

  get selectedItems(): T[] {
    return this.filteredItems.filter((item) => this.state.selectedItems.has(this.getItemId(item)));
  }

  get hasFilters(): boolean {
    return Object.values(this.state.filters).some(
      (value) => value !== null && value !== undefined && value !== "",
    );
  }

  // Filter operations
  protected applyFilter(items: T[], _key: keyof Filters, _value: any): T[] {
    // Override in derived class to implement custom filters
    return items;
  }

  // Action methods
  actionSort(field: string): void {
    if (this.state.sortField === field) {
      this.state.sortDirection = this.state.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.state.sortField = field;
      this.state.sortDirection = "asc";
    }
  }

  actionSetFilter<K extends keyof Filters>(key: K, value: Filters[K]): void {
    this.state.filters[key] = value;
  }

  actionClearFilters(): void {
    this.state.filters = {};
  }

  actionSelectItem(item: T): void {
    this.state.selectedItems.add(this.getItemId(item));
  }

  actionDeselectItem(item: T): void {
    this.state.selectedItems.delete(this.getItemId(item));
  }

  actionSelectAll(checked: boolean): void {
    if (checked) {
      this.filteredItems.forEach((item) => {
        this.state.selectedItems.add(this.getItemId(item));
      });
    } else {
      this.state.selectedItems.clear();
    }
  }

  actionToggleSelection(item: T): void {
    const id = this.getItemId(item);
    if (this.state.selectedItems.has(id)) {
      this.state.selectedItems.delete(id);
    } else {
      this.state.selectedItems.add(id);
    }
  }
  /**
   * Get the unique storage key for this table controller instance
   * @returns A unique key based on the controller's constructor name
   */
  private getStorageKey(): string {
    return STORAGE_KEY_PREFIX + this.constructor.name;
  }

  /**
   * Save filter state to session storage
   * @param filterState The filter state to save
   */
  private saveFilterState(
    filterState: Pick<TableState<T>, "sortField" | "sortDirection" | "filters" | "pageSize" | "currentPage">,
  ): void {
    try {
      sessionStorage.setItem(this.getStorageKey(), JSON.stringify(filterState));
    } catch (error) {
      console.error("Failed to save table filter state to session storage:", error);
    }
  }

  /** When pagination is enabled (pageSize set), returns the slice of filteredItems for the current page. */
  get visibleItems(): T[] {
    const pageSize = this.state.pageSize;
    if (pageSize == null || pageSize < 1) return this.state.filteredItems;
    const total = this.state.filteredItems.length;
    const page = Math.max(1, Math.min(this.state.currentPage ?? 1, Math.ceil(total / pageSize) || 1));
    const start = (page - 1) * pageSize;
    return this.state.filteredItems.slice(start, start + pageSize);
  }

  /** Total number of pages when pagination is enabled; 1 when disabled. */
  get totalPages(): number {
    const pageSize = this.state.pageSize;
    if (pageSize == null || pageSize < 1) return 1;
    return Math.ceil(this.state.filteredItems.length / pageSize) || 1;
  }

  get paginationEnabled(): boolean {
    return this.state.pageSize != null && this.state.pageSize > 0;
  }

  actionSetPage(page: number): void {
    const p = Math.max(1, Math.min(page, this.totalPages));
    this.state.currentPage = p;
  }

  actionSetPageSize(size: number): void {
    this.state.pageSize = size;
    this.state.currentPage = 1;
  }

  /**
   * Load filter state from session storage
   * @returns The saved filter state or null if not found
   */
  private loadFilterState(): Pick<
    TableState<T>,
    "sortField" | "sortDirection" | "filters" | "pageSize" | "currentPage"
  > | null {
    try {
      const savedState = sessionStorage.getItem(this.getStorageKey());
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error("Failed to load table filter state from session storage:", error);
    }
    return null;
  }
}
